import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PageShell from '../components/ui/PageShell';
import Attachments from '../components/Attachments';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import { Globals } from '../globalfunctions.js';
import { subscribeToTicketEvents } from '../utils/ticketEventStream';
import { apiClient, ApiError } from '../utils/apiClient';
import '../styles/ticketTheme.css';
import { cleanEmailText, needsReadMore } from '../utils/emailText';
import EmailText from '../components/EmailText';
import OriginalEmailModal from '../components/OriginalEmailModal';
import ActivityFiles from '../components/ActivityFiles';
import ReplyFiles from '../components/ReplyFiles';
import FollowUps from '../components/FollowUps';
import { activeMention, filterStaff, insertMention, staffName } from '../utils/mentions';
import { addReplyFiles, filesFromPaste, uploadTicketFiles } from '../utils/ticketFiles';
import { fillTemplate, templateContext, TEMPLATE_VARIABLES } from '../utils/templateVars';
import { suggestArticles } from '../utils/kbSuggest';
import './TicketDetail.css';
import { alertDialog } from '../utils/dialogs';

const STATUS_OPTIONS = ['open', 'in_progress', 'pending', 'resolved', 'closed'];
const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Awaiting Reply from User',
  resolved: 'Resolved',
  closed: 'Closed'
};
const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const TYPE_OPTIONS = ['support', 'feature_request', 'bug', 'maintenance', 'consultation'];
const TYPE_LABELS = {
  support: 'Support',
  feature_request: 'Feature Request',
  bug: 'Bug',
  maintenance: 'Maintenance',
  consultation: 'Consultation'
};

function TicketDetail() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [activities, setActivities] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [error, setError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replyType, setReplyType] = useState('external');
  const [sendingReply, setSendingReply] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardTo, setForwardTo] = useState('');
  const [forwardMessage, setForwardMessage] = useState('');
  const [forwarding, setForwarding] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCannedModal, setShowCannedModal] = useState(false);
  const [newCannedTitle, setNewCannedTitle] = useState('');
  const [newCannedBody, setNewCannedBody] = useState('');
  const [savingCanned, setSavingCanned] = useState(false);
  const [cannedToDelete, setCannedToDelete] = useState(null);
  const [deletingCanned, setDeletingCanned] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [categoryInput, setCategoryInput] = useState('');
  // The newest activity (index 0, post-reorder) is always shown in full;
  // everything older starts collapsed to a one-line summary so the page
  // doesn't turn into an endless scroll of the whole conversation history.
  const [expandedActivityIds, setExpandedActivityIds] = useState(new Set());
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [originalEmailId, setOriginalEmailId] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [kbArticles, setKbArticles] = useState([]);
  const [replyFiles, setReplyFiles] = useState([]);
  const [replyFileError, setReplyFileError] = useState('');
  // Bumped after a reply with files so the Files panel reloads.
  const [filesVersion, setFilesVersion] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTarget, setMergeTarget] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [newCannedStatus, setNewCannedStatus] = useState('');
  const [newCannedPriority, setNewCannedPriority] = useState('');
  const replyRef = useRef(null);
  // @mention suggestions while typing an internal note: { mention, caret, index } or null
  const [mentionState, setMentionState] = useState(null);
  const mentionMatches = mentionState ? filterStaff(staff, mentionState.mention.query) : [];

  const fetchTicketDetails = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/tickets');
      const foundTicket = data.find(t => t.ticket_id?.toString() === ticketId);
      if (foundTicket) {
        setTicket(foundTicket);
      } else {
        setError('Ticket not found');
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      setError(err instanceof ApiError ? 'Failed to fetch ticket details' : 'Error fetching ticket details');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  const fetchTicketActivities = useCallback(async () => {
    setLoadingActivities(true);
    try {
      // The API already orders newest-first - that's also the order we
      // want on screen (latest reply at the top), so no re-sort needed.
      const data = await apiClient.get(`/api/activities/ticket/${ticketId}`);
      setActivities(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching ticket activities:', err);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, [ticketId]);

  // Status changes (empty until the API's history table exists).
  const fetchStatusHistory = useCallback(async () => {
    try {
      const data = await apiClient.get(`/api/tickets/${ticketId}/history`);
      setStatusHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setStatusHistory([]);
    }
  }, [ticketId]);

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }

    if (ticketId) {
      fetchTicketDetails();
      fetchTicketActivities();
      fetchStatusHistory();
    }

    apiClient.post('/api/users/findall')
      .then((data) => setStaff(Array.isArray(data) ? data.filter((u) => (u.role_type || '').toLowerCase() !== 'customer') : []))
      .catch(() => setStaff([]));

    fetchCannedResponses();
    apiClient.get('/api/kb').then((data) => setKbArticles(Array.isArray(data) ? data : [])).catch(() => setKbArticles([]));
  }, [ticketId, navigate, fetchTicketDetails, fetchTicketActivities, fetchStatusHistory]);

  // Keeps the free-text category input in sync whenever a fresh ticket
  // loads (including after another field's update refetches it), without
  // fighting the user's keystrokes on every unrelated refetch in between.
  useEffect(() => {
    setCategoryInput(ticket?.category || '');
  }, [ticket?.ticket_id, ticket?.category]);

  // Landing on a different ticket (e.g. after a merge) starts collapsed again.
  useEffect(() => {
    setShowAllActivities(false);
    setExpandedActivityIds(new Set());
  }, [ticketId]);

  const fetchCannedResponses = async () => {
    try {
      setCannedResponses(await apiClient.get('/api/cannedResponses'));
    } catch (err) {
      console.error('Error fetching canned responses:', err);
    }
  };

  const insertCannedResponse = (id) => {
    const canned = cannedResponses.find((c) => c.id === parseInt(id, 10));
    if (!canned) return;
    const body = fillTemplate(canned.body, templateContext(ticket, `${Globals.FirstName || ''} ${Globals.LastName || ''}`.trim()));
    setReplyText((prev) => (prev.trim() ? `${prev}\n\n${body}` : body));

    // Macro: a canned response can also move the ticket's status/priority.
    const patch = {};
    if (canned.set_status && canned.set_status !== ticket?.status) patch.status = canned.set_status;
    if (canned.set_priority && canned.set_priority !== ticket?.priority) patch.priority = canned.set_priority;
    if (Object.keys(patch).length > 0) updateTicket(patch);
  };

  // Articles that look relevant to this ticket (subject + what the customer wrote).
  const suggestions = ticket ? suggestArticles(kbArticles, `${ticket.subject || ''} ${cleanEmailText(ticket.description || '').reply.slice(0, 600)}`) : [];

  const insertArticle = (article) => {
    const text = `${article.title}\n\n${article.body}`;
    setReplyText((prev) => (prev.trim() ? `${prev}\n\n${text}` : text));
    replyRef.current?.focus();
  };

  const createCannedResponse = async (e) => {
    e.preventDefault();
    if (!newCannedTitle.trim() || !newCannedBody.trim()) return;
    setSavingCanned(true);
    try {
      await apiClient.post('/api/cannedResponses', {
        title: newCannedTitle,
        body: newCannedBody,
        set_status: newCannedStatus || null,
        set_priority: newCannedPriority || null
      });
      setNewCannedTitle('');
      setNewCannedBody('');
      setNewCannedStatus('');
      setNewCannedPriority('');
      fetchCannedResponses();
    } catch (err) {
      console.error('Error creating canned response:', err);
    } finally {
      setSavingCanned(false);
    }
  };

  const confirmDeleteCanned = async () => {
    if (!cannedToDelete) return;
    setDeletingCanned(true);
    try {
      await apiClient.delete(`/api/cannedResponses/${cannedToDelete.id}`);
      setCannedToDelete(null);
      fetchCannedResponses();
    } catch (err) {
      console.error('Error deleting canned response:', err);
    } finally {
      setDeletingCanned(false);
    }
  };

  // Live updates for this ticket (status changes, replies from either
  // side) plus agent-collision presence. 'stream-open' covers first
  // connect and any reconnect - events during a drop are gone for good,
  // so a fresh connection means "resync now", not just "listen again".
  useEffect(() => {
    if (!ticketId) return undefined;

    const unsubscribeEvents = subscribeToTicketEvents((event) => {
      if (event.type === 'stream-open') {
        fetchTicketDetails();
        fetchTicketActivities();
        return;
      }
      if (event.type === 'presence' && event.payload?.ticketId === ticketId) {
        setViewers(event.payload.viewers || []);
        return;
      }
      if (event.type === 'ticket-updated' && event.payload?.ticket_id === ticketId) {
        fetchTicketDetails();
        fetchStatusHistory();
      }
      if (event.type === 'ticket-activity' && event.payload?.ticket_id === ticketId) {
        fetchTicketDetails();
        fetchTicketActivities();
        fetchStatusHistory();
      }
      if (event.type === 'tickets-updated' && event.payload?.ticket_ids?.includes(ticketId)) {
        fetchTicketDetails();
      }
    });

    const sendHeartbeat = async () => {
      try {
        const data = await apiClient.post(`/api/tickets/${ticketId}/viewing`);
        setViewers(data.viewers || []);
      } catch (err) {
        // Non-critical - presence is a nice-to-have, skip this beat and try again next interval
      }
    };

    sendHeartbeat();
    const heartbeatInterval = setInterval(sendHeartbeat, 10000);

    return () => {
      unsubscribeEvents();
      clearInterval(heartbeatInterval);
    };
  }, [ticketId, fetchTicketDetails, fetchTicketActivities, fetchStatusHistory]);

  // Shortcuts: r = jump to the reply box, e = resolve, ? = help. Ignored
  // while typing in a field or with a dialog open.
  const anyModalOpen = showForwardModal || showCannedModal || !!cannedToDelete || showDeleteConfirm || showMergeModal || showShortcuts;
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || anyModalOpen) return;
      const tag = e.target.tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || e.target.isContentEditable) return;

      if (e.key === 'r') {
        e.preventDefault();
        replyRef.current?.focus();
        replyRef.current?.scrollIntoView({ block: 'center' });
      } else if (e.key === 'e' && ticket && !['resolved', 'closed'].includes(ticket.status)) {
        updateTicket({ status: 'resolved' });
      } else if (e.key === '?') {
        setShowShortcuts(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyModalOpen, ticket]);

  const submitMerge = async (e) => {
    e.preventDefault();
    setMerging(true);
    setMergeError('');
    try {
      const result = await apiClient.post(`/api/tickets/${ticketId}/merge`, { target_ticket_number: mergeTarget });
      setShowMergeModal(false);
      navigate(`/tickets/${result.target_ticket_id}`);
    } catch (err) {
      setMergeError(err.message || 'Failed to merge tickets');
    } finally {
      setMerging(false);
    }
  };

  const updateTicket = async (patch) => {
    try {
      await apiClient.put(`/api/tickets/${ticketId}`, patch);
      fetchTicketDetails();
    } catch (err) {
      console.error('Error updating ticket:', err);
    }
  };

  const confirmDeleteTicket = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/api/tickets/${ticketId}`);
      navigate('/tickets');
    } catch (err) {
      console.error('Error deleting ticket:', err);
      alertDialog('Failed to delete ticket');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const addTag = (e) => {
    e.preventDefault();
    const value = newTag.trim();
    if (!value) return;
    const current = Array.isArray(ticket.tags) ? ticket.tags : [];
    if (current.includes(value)) {
      setNewTag('');
      return;
    }
    updateTicket({ tags: [...current, value] });
    setNewTag('');
  };

  const removeTag = (tag) => {
    const current = Array.isArray(ticket.tags) ? ticket.tags : [];
    updateTicket({ tags: current.filter((t) => t !== tag) });
  };

  const resendActivityEmail = async (activityId) => {
    try {
      await apiClient.post(`/api/activities/${activityId}/resend-email`);
      fetchTicketActivities();
    } catch (err) {
      alertDialog(err.message || 'Email still failed to send');
    }
  };

  const pickMention = (person) => {
    if (!mentionState) return;
    const result = insertMention(replyText, mentionState.mention, mentionState.caret, person);
    setReplyText(result.text);
    setMentionState(null);
    requestAnimationFrame(() => {
      const box = replyRef.current;
      if (box) { box.focus(); box.setSelectionRange(result.caret, result.caret); }
    });
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSendingReply(true);
    setReplyFileError('');
    try {
      // Files go to the ticket first; the reply then names them, and the API
      // emails them to the customer (external replies).
      const attachment_ids = replyFiles.length > 0 ? await uploadTicketFiles(ticketId, replyFiles) : undefined;
      await apiClient.post('/api/activities', {
        ticket_id: ticketId,
        author_user_id: Globals.Id,
        customer_id: ticket?.customer_user_id,
        internal_external: replyType,
        activity_type: replyType === 'external' ? 'email' : 'note',
        description: replyText,
        ...(attachment_ids ? { attachment_ids } : {})
      });
      if (attachment_ids) setFilesVersion((v) => v + 1);
      setReplyFiles([]);
      setReplyText('');
      fetchTicketActivities();
      fetchTicketDetails();
    } catch (err) {
      console.error('Error sending reply:', err);
      setReplyFileError(err.message || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const submitForward = async (e) => {
    e.preventDefault();
    setForwarding(true);
    try {
      await apiClient.post(`/api/tickets/${ticketId}/forward`, { to: forwardTo, message: forwardMessage });
      setShowForwardModal(false);
      setForwardTo('');
      setForwardMessage('');
    } catch (err) {
      console.error('Error forwarding ticket:', err);
      alertDialog('Failed to forward ticket');
    } finally {
      setForwarding(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatDateInput = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toISOString().slice(0, 10);
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'status-open';
      case 'in_progress': case 'in-progress': return 'status-in-progress';
      case 'pending': return 'status-pending';
      case 'resolved': return 'status-resolved';
      case 'closed': return 'status-closed';
      default: return 'status-default';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent': case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-default';
    }
  };

  const getInitials = (name) => {
    if (!name) return 'NA';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const toggleActivityExpanded = (id) => {
    setExpandedActivityIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // How many entries show before "Show all"; each is clamped to five lines
  // (see .is-clamped), so five entries is a short, scannable block. Phones
  // get fewer - the "show all" button is one tap away.
  const ACTIVITY_PREVIEW_COUNT = window.matchMedia('(max-width: 768px)').matches ? 3 : 5;

  // Due date is the one real deadline the data model has - used in place
  // of a formal SLA engine, which this app doesn't have. Closed/resolved
  // tickets don't need an "overdue" scare once the work is actually done.
  const getDueStatus = () => {
    if (!ticket?.due_date) return null;
    const isDone = ['resolved', 'closed'].includes(ticket.status);
    const due = new Date(ticket.due_date);
    const diffMs = due - new Date();
    if (isDone) return { label: `Due ${formatDateTime(ticket.due_date)}`, className: 'due-done' };
    if (diffMs < 0) return { label: `Overdue since ${formatDateTime(ticket.due_date)}`, className: 'due-overdue' };
    const hoursLeft = diffMs / (1000 * 60 * 60);
    if (hoursLeft <= 24) return { label: `Due ${formatDateTime(ticket.due_date)} (soon)`, className: 'due-soon' };
    return { label: `Due ${formatDateTime(ticket.due_date)}`, className: 'due-ok' };
  };

  if (loading) {
    return <PageShell contentClassName="tk"><div className="loading" aria-live="polite">Loading ticket details&hellip;</div></PageShell>;
  }

  if (error || !ticket) {
    return (
      <PageShell contentClassName="tk">
        <div className="error">{error || 'Ticket not found'}</div>
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </PageShell>
    );
  }

  const dueStatus = getDueStatus();
  const tags = Array.isArray(ticket.tags) ? ticket.tags : [];

  return (
    <PageShell contentClassName="tk">
      <div className="ticket-detail-container">
        <div className="ticket-detail-header">
          <div className="header-left">
            <button type="button" onClick={() => navigate(-1)} className="back-btn">
              <i className="fas fa-arrow-left" aria-hidden="true"></i> Back
            </button>
            <div className="header-title">
              <h1>{ticket.subject || `Ticket #${ticket.ticket_number}`}</h1>
              <div className="header-subline">
                <span>#{ticket.ticket_number}</span>
                <span aria-hidden="true">&middot;</span>
                <Link to={`/customer/${ticket.customer_user_id}`}>{ticket.customer_name || 'Unknown Customer'}</Link>
              </div>
            </div>
          </div>
          <div className="header-right">
            <span className={`status-badge ${getStatusClass(ticket.status)}`}>{STATUS_LABELS[ticket.status] || ticket.status}</span>
            <span className={`priority-badge ${getPriorityClass(ticket.priority)}`}>{ticket.priority}</span>
            <Button variant="secondary" onClick={() => setShowForwardModal(true)}>
              <i className="fas fa-share" aria-hidden="true"></i> Forward
            </Button>
            <Button variant="secondary" onClick={() => { setMergeTarget(''); setMergeError(''); setShowMergeModal(true); }}>
              <i className="fas fa-code-merge" aria-hidden="true"></i> Merge
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteConfirm(true)}>
              <i className="fas fa-trash" aria-hidden="true"></i> Delete
            </Button>
          </div>
        </div>

        {viewers.length > 0 && (
          <div className="collision-banner" role="status">
            <i className="fas fa-eye" aria-hidden="true"></i>
            {viewers.length === 1
              ? `${viewers[0].name} is also viewing this ticket right now.`
              : `${viewers.map((v) => v.name).join(', ')} are also viewing this ticket right now.`}
          </div>
        )}

        <div className="ticket-info-grid">
          <div className="ticket-main-info">
            <div className="info-card">
              <div className="ticket-meta">
                <div className="meta-item"><i className="fas fa-calendar" aria-hidden="true"></i><span>Created: {formatDateTime(ticket.created_at)}</span></div>
                <div className="meta-item"><i className="fas fa-clock" aria-hidden="true"></i><span>Updated: {formatDateTime(ticket.updated_at)}</span></div>
              </div>
              {ticket.description && (
                <div className="ticket-description">
                  <h3>Description</h3>
                  <p><EmailText text={cleanEmailText(ticket.description).full} /></p>
                </div>
              )}
            </div>

            <div className="reply-section info-card">
              <h3>Reply</h3>
              <form onSubmit={submitReply}>
                <div className="reply-type-toggle">
                  <label>
                    <input type="radio" name="replyType" value="external" checked={replyType === 'external'} onChange={() => setReplyType('external')} />
                    Reply to Customer (sends an email)
                  </label>
                  <label>
                    <input type="radio" name="replyType" value="internal" checked={replyType === 'internal'} onChange={() => setReplyType('internal')} />
                    Internal Note (staff only)
                  </label>
                </div>
                <div className="canned-response-bar">
                  <label className="sr-only" htmlFor="canned-response-select">Insert canned response</label>
                  <select
                    id="canned-response-select"
                    className="canned-response-select"
                    value=""
                    onChange={(e) => { if (e.target.value) insertCannedResponse(e.target.value); e.target.value = ''; }}
                  >
                    <option value="">Insert canned response&hellip;</option>
                    {cannedResponses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                  </select>
                  <button type="button" className="canned-response-manage-btn" onClick={() => setShowCannedModal(true)}>
                    Manage
                  </button>
                </div>
                {replyType === 'external' && suggestions.length > 0 && (
                  <div className="kb-suggestions">
                    <span className="kb-suggestions-label">Suggested articles</span>
                    {suggestions.map((article) => (
                      <button key={article.id} type="button" className="kb-suggestion" title="Insert this article into your reply" onClick={() => insertArticle(article)}>
                        <i className="fas fa-book-open" aria-hidden="true"></i> {article.title}
                      </button>
                    ))}
                  </div>
                )}
                <label className="sr-only" htmlFor="reply-textarea">{replyType === 'external' ? 'Reply to customer' : 'Internal note'}</label>
                <textarea
                  id="reply-textarea"
                  ref={replyRef}
                  className="reply-textarea"
                  rows={4}
                  placeholder={replyType === 'external' ? 'Write a reply to the customer…' : 'Add an internal note…'}
                  value={replyText}
                  onChange={(e) => {
                    setReplyText(e.target.value);
                    const caret = e.target.selectionStart;
                    const mention = replyType === 'internal' ? activeMention(e.target.value, caret) : null;
                    setMentionState(mention ? { mention, caret, index: 0 } : null);
                  }}
                  onBlur={() => setTimeout(() => setMentionState(null), 150)}
                  onKeyDown={(e) => {
                    if (!mentionState || mentionMatches.length === 0) return;
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      const step = e.key === 'ArrowDown' ? 1 : -1;
                      setMentionState((m) => ({ ...m, index: (m.index + step + mentionMatches.length) % mentionMatches.length }));
                    } else if (e.key === 'Enter' || e.key === 'Tab') {
                      e.preventDefault();
                      pickMention(mentionMatches[mentionState.index]);
                    } else if (e.key === 'Escape') {
                      setMentionState(null);
                    }
                  }}
                  aria-autocomplete={replyType === 'internal' ? 'list' : undefined}
                  onPaste={(e) => {
                    const pasted = filesFromPaste(e);
                    if (pasted.length === 0) return;
                    e.preventDefault();
                    const result = addReplyFiles(replyFiles, pasted);
                    setReplyFiles(result.files);
                    setReplyFileError(result.error);
                  }}
                />
                {mentionState && mentionMatches.length > 0 && (
                  <ul className="mention-list" role="listbox" aria-label="Mention a colleague">
                    {mentionMatches.map((person, i) => (
                      <li key={person.id}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={i === mentionState.index}
                          onMouseDown={(e) => { e.preventDefault(); pickMention(person); }}
                        >{staffName(person)}</button>
                      </li>
                    ))}
                  </ul>
                )}
                <ReplyFiles
                  files={replyFiles}
                  error={replyFileError}
                  disabled={sendingReply}
                  onAdd={(incoming) => {
                    const result = addReplyFiles(replyFiles, incoming);
                    setReplyFiles(result.files);
                    setReplyFileError(result.error);
                  }}
                  onRemove={(i) => setReplyFiles((files) => files.filter((_, idx) => idx !== i))}
                />
                <Button type="submit" disabled={sendingReply || !replyText.trim()}>
                  {sendingReply ? 'Sending…' : replyType === 'external' ? 'Send Reply' : 'Add Note'}
                </Button>
              </form>
            </div>

            <div className="activities-section">
              <div className="section-header">
                <h2><i className="fas fa-history" aria-hidden="true"></i> Activity Timeline</h2>
                {loadingActivities && <div className="loading-spinner" aria-live="polite">Loading&hellip;</div>}
              </div>

              <div className="activities-container">
                {activities.length === 0 ? (
                  <div className="no-activities"><i className="fas fa-inbox" aria-hidden="true"></i><p>No activity yet.</p></div>
                ) : (
                  <div className="activities-timeline">
                    {(showAllActivities ? activities : activities.slice(0, ACTIVITY_PREVIEW_COUNT)).map((activity, index) => {
                      const id = activity.id || index;
                      // Each entry shows what was actually written, clamped to
                      // five lines; "Read more" reveals the whole original
                      // (quoted thread, signature and all).
                      const body = cleanEmailText(activity.description || 'No description provided');
                      const isExpanded = expandedActivityIds.has(id);
                      const canExpand = needsReadMore(body);

                      return (
                        <div key={id} className={`activity-item ${activity.internal_external === 'internal' ? 'internal-note' : ''}`}>
                          <div className="activity-avatar">{getInitials(activity.author_name || 'Unknown')}</div>
                          <div className="activity-content">
                            <div className="activity-header">
                              <div className="activity-author">
                                <strong>{activity.author_name || 'Unknown'}</strong>
                                <span className="activity-type">{activity.activity_type || 'Activity'}</span>
                                {activity.internal_external === 'internal' && <span className="internal-badge">Internal</span>}
                                {activity.additional_data?.email_error && (
                                  <span className="internal-badge" style={{ background: '#f8d7da', color: '#842029' }} title={activity.additional_data.email_error}>
                                    Email not sent
                                    {' '}
                                    <button type="button" className="activity-toggle" onClick={() => resendActivityEmail(activity.id)}>Resend</button>
                                  </span>
                                )}
                              </div>
                              <div className="activity-date">{formatDateTime(activity.created_at)}</div>
                            </div>
                            <div className={`activity-description ${canExpand && !isExpanded ? 'is-clamped' : ''}`}>
                              <EmailText text={isExpanded ? body.full : body.reply} />
                            </div>
                            {canExpand && (
                              <button type="button" className="activity-toggle" onClick={() => toggleActivityExpanded(id)} aria-expanded={isExpanded}>
                                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden="true"></i> {isExpanded ? 'Show less' : 'Read more'}
                              </button>
                            )}
                            <ActivityFiles files={activity.additional_data?.files} />
                            {activity.additional_data?.has_original_html && (
                              <button type="button" className="activity-toggle" onClick={() => setOriginalEmailId(activity.id)}>
                                <i className="fas fa-envelope-open-text" aria-hidden="true"></i> View original email
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {activities.length > ACTIVITY_PREVIEW_COUNT && (
                      <button type="button" className="activity-show-all" onClick={() => setShowAllActivities((v) => !v)} aria-expanded={showAllActivities}>
                        {showAllActivities
                          ? 'Show fewer'
                          : `Show all ${activities.length} activities (${activities.length - ACTIVITY_PREVIEW_COUNT} older)`}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="ticket-sidebar-info">
            <div className="info-card">
              <h3>Ticket Details</h3>
              <FormField id="ticket-status" label="Status">
                <select
                  id="ticket-status"
                  value={ticket.status}
                  onChange={(e) => updateTicket({ status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </FormField>
              <FormField id="ticket-priority" label="Priority">
                <select
                  id="ticket-priority"
                  value={ticket.priority}
                  onChange={(e) => updateTicket({ priority: e.target.value })}
                >
                  {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
                </select>
              </FormField>
              <FormField id="ticket-type" label="Type">
                <select
                  id="ticket-type"
                  value={ticket.ticket_type || 'support'}
                  onChange={(e) => updateTicket({ ticket_type: e.target.value })}
                >
                  {TYPE_OPTIONS.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </FormField>
              <FormField id="ticket-category" label="Category">
                <input
                  id="ticket-category"
                  type="text"
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onBlur={() => { if (categoryInput !== (ticket.category || '')) updateTicket({ category: categoryInput || null }); }}
                  placeholder="e.g. Billing, Hosting, Access…"
                />
              </FormField>
              <FormField id="assigned-to" label="Assigned To">
                <select
                  id="assigned-to"
                  value={ticket.assigned_user_id || ''}
                  onChange={(e) => updateTicket({ assigned_user_id: e.target.value || null })}
                >
                  <option value="">Unassigned</option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
                </select>
              </FormField>
              <FormField id="ticket-due-date" label="Due Date">
                <input
                  id="ticket-due-date"
                  type="date"
                  value={formatDateInput(ticket.due_date)}
                  onChange={(e) => updateTicket({ due_date: e.target.value ? new Date(e.target.value).toISOString() : null })}
                />
              </FormField>
              {dueStatus && <div className={`due-indicator ${dueStatus.className}`}>{dueStatus.label}</div>}

              <div className="ticket-tags-field">
                <span className="field-label">Tags</span>
                <div className="ticket-tags-list">
                  {tags.length === 0 && <span className="ticket-tags-empty">No tags</span>}
                  {tags.map((tag) => (
                    <span key={tag} className="ticket-tag-chip">
                      {tag}
                      <button type="button" onClick={() => removeTag(tag)} aria-label={`Remove tag ${tag}`}>&times;</button>
                    </span>
                  ))}
                </div>
                <form className="ticket-tag-add" onSubmit={addTag}>
                  <label className="sr-only" htmlFor="ticket-new-tag">Add a tag</label>
                  <input id="ticket-new-tag" type="text" value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Add a tag…" />
                  <button type="submit" disabled={!newTag.trim()}>Add</button>
                </form>
              </div>
            </div>

            <FollowUps ticketId={ticketId} />

            {statusHistory.length > 1 && (
              <div className="info-card">
                <h3>Status history</h3>
                <ol className="status-history">
                  {statusHistory.map((row) => (
                    <li key={row.id}>
                      <span className="status-history-change">
                        {row.from_status ? `${STATUS_LABELS[row.from_status] || row.from_status} → ` : 'Opened as '}
                        <strong>{STATUS_LABELS[row.to_status] || row.to_status}</strong>
                      </span>
                      <span className="status-history-meta">{formatDateTime(row.created_at)}{row.changed_by ? ` · ${row.changed_by}` : ''}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="info-card">
              <h3>Time &amp; Satisfaction</h3>
              <div className="time-tracking-row">
                <FormField id="estimated-hours" label="Estimated Hrs">
                  <input
                    id="estimated-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    inputMode="decimal"
                    defaultValue={ticket.estimated_hours ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value);
                      if (value !== (ticket.estimated_hours ?? null)) updateTicket({ estimated_hours: value });
                    }}
                  />
                </FormField>
                <FormField id="actual-hours" label="Actual Hrs">
                  <input
                    id="actual-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    inputMode="decimal"
                    defaultValue={ticket.actual_hours ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value === '' ? null : Number(e.target.value);
                      if (value !== (ticket.actual_hours ?? null)) updateTicket({ actual_hours: value });
                    }}
                  />
                </FormField>
              </div>
              <div className="csat-row">
                <span className="field-label">Customer Satisfaction</span>
                {ticket.satisfaction_rating ? (
                  <div className="csat-stars" title={`Rated ${ticket.satisfaction_rating} of 5`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <i key={n} className={`fas fa-star ${n <= ticket.satisfaction_rating ? 'csat-star-filled' : 'csat-star-empty'}`} aria-hidden="true"></i>
                    ))}
                    <span className="sr-only">{`Rated ${ticket.satisfaction_rating} of 5 stars`}</span>
                  </div>
                ) : (
                  <span className="csat-pending">
                    {ticket.satisfaction_requested_at ? 'Survey sent, no response yet' : 'Not yet requested'}
                  </span>
                )}
              </div>
            </div>

            <div className="info-card">
              <Attachments key={filesVersion} entityType="ticket" entityId={ticket.ticket_id} />
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        title="Forward Ticket"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowForwardModal(false)}>Cancel</Button>
            <Button type="submit" form="forward-form" disabled={forwarding || !forwardTo.trim()}>{forwarding ? 'Sending…' : 'Forward'}</Button>
          </>
        )}
      >
        <form id="forward-form" onSubmit={submitForward}>
          <FormField id="forward-to" label="Forward To" required>
            <input id="forward-to" type="email" autoComplete="email" value={forwardTo} onChange={(e) => setForwardTo(e.target.value)} required />
          </FormField>
          <FormField id="forward-message" label="Note (optional)">
            <textarea id="forward-message" rows={3} value={forwardMessage} onChange={(e) => setForwardMessage(e.target.value)} />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={showCannedModal}
        onClose={() => setShowCannedModal(false)}
        title="Canned Responses"
      >
        <div className="canned-response-list">
          {cannedResponses.length === 0 && <p className="canned-response-empty">No canned responses yet - add one below.</p>}
          {cannedResponses.map((c) => (
            <div key={c.id} className="canned-response-item">
              <div>
                <div className="canned-response-item-title">{c.title}</div>
                {(c.set_status || c.set_priority) && (
                  <div className="canned-response-macro">
                    Also sets:{c.set_status ? ` status → ${STATUS_LABELS[c.set_status]}` : ''}{c.set_status && c.set_priority ? ',' : ''}{c.set_priority ? ` priority → ${c.set_priority}` : ''}
                  </div>
                )}
                <div className="canned-response-item-body">{c.body}</div>
              </div>
              <Button variant="danger" onClick={() => setCannedToDelete(c)}>Delete</Button>
            </div>
          ))}
        </div>

        <form onSubmit={createCannedResponse} className="canned-response-form">
          <FormField id="canned-title" label="Title" required>
            <input id="canned-title" type="text" placeholder="e.g. Requesting more info" value={newCannedTitle} onChange={(e) => setNewCannedTitle(e.target.value)} />
          </FormField>
          <FormField id="canned-body" label="Response" required>
            <textarea id="canned-body" rows={4} value={newCannedBody} onChange={(e) => setNewCannedBody(e.target.value)} />
          </FormField>
          <p className="canned-response-vars">
            Fills in when inserted: {TEMPLATE_VARIABLES.map((v) => `{{${v}}}`).join(' ')}
          </p>
          <div className="canned-response-macro-fields">
            <FormField id="canned-status" label="Also set status (optional)">
              <select id="canned-status" value={newCannedStatus} onChange={(e) => setNewCannedStatus(e.target.value)}>
                <option value="">No change</option>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
            </FormField>
            <FormField id="canned-priority" label="Also set priority (optional)">
              <select id="canned-priority" value={newCannedPriority} onChange={(e) => setNewCannedPriority(e.target.value)}>
                <option value="">No change</option>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </select>
            </FormField>
          </div>
          <Button type="submit" disabled={savingCanned || !newCannedTitle.trim() || !newCannedBody.trim()}>
            {savingCanned ? 'Saving…' : 'Add Canned Response'}
          </Button>
        </form>
      </Modal>

      <Modal
        open={!!cannedToDelete}
        onClose={() => setCannedToDelete(null)}
        title="Delete Canned Response"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setCannedToDelete(null)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDeleteCanned} disabled={deletingCanned}>{deletingCanned ? 'Deleting…' : 'Delete'}</Button>
          </>
        )}
      >
        <p>Delete the canned response <strong>{cannedToDelete?.title}</strong>? This cannot be undone.</p>
      </Modal>

      <Modal
        open={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        title="Merge Ticket"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowMergeModal(false)}>Cancel</Button>
            <Button type="submit" form="merge-form" disabled={merging || !mergeTarget.trim()}>{merging ? 'Merging…' : 'Merge'}</Button>
          </>
        )}
      >
        <form id="merge-form" onSubmit={submitMerge}>
          <p>Move all of ticket <strong>#{ticket.ticket_number}</strong>'s history into another ticket and close this one. This can't be undone.</p>
          {mergeError && <p className="merge-error" role="alert">{mergeError}</p>}
          <FormField id="merge-target" label="Merge into ticket number" required>
            <input id="merge-target" type="number" min="1" inputMode="numeric" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} placeholder="e.g. 142" />
          </FormField>
        </form>
      </Modal>

      <Modal
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        title="Keyboard Shortcuts"
        footer={<Button onClick={() => setShowShortcuts(false)}>Done</Button>}
      >
        <dl className="shortcut-list">
          <dt><kbd>r</kbd></dt><dd>Jump to the reply box</dd>
          <dt><kbd>e</kbd></dt><dd>Mark the ticket resolved</dd>
          <dt><kbd>?</kbd></dt><dd>Show this help</dd>
        </dl>
      </Modal>

      <Modal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Ticket"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
            <Button variant="danger" onClick={confirmDeleteTicket} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete Ticket'}</Button>
          </>
        )}
      >
        <p>Permanently delete ticket <strong>#{ticket.ticket_number}</strong>? This cannot be undone.</p>
      </Modal>

      <OriginalEmailModal activityId={originalEmailId} onClose={() => setOriginalEmailId(null)} />
    </PageShell>
  );
}

export default TicketDetail;
